/**
 * A nota da consulta escrita pela IA — os tipos e as contas puras.
 *
 * A IA escuta a consulta inteira (com consentimento do paciente) ou o ditado do
 * profissional, transcreve em partes, sugere as respostas da ficha e o resumo
 * da consulta. Nada disso é prontuário até o profissional aprovar
 * (`aprovar_nota_de_ia`, 0155). O áudio não vai para o servidor como arquivo:
 * cada parte fica no aparelho até ser transcrita (`comuns/filaDeAudio`).
 */

import type { RespostaDaFicha } from './tipos';

export type ModoDaNota = 'consulta' | 'ditado';

export type SituacaoDaNota = 'gravando' | 'gerando' | 'revisao' | 'aprovada' | 'descartada' | 'falhou';

export interface NotaDeIa {
    id: string;
    atendimentoId: string;
    contatoId: string;
    modo: ModoDaNota;
    situacao: SituacaoDaNota;
    /** A ficha que a IA preenche. */
    modeloId?: string;
    consentimentoEm?: string;
    /** O que a IA propôs — não é prontuário. */
    sugestao?: Record<string, RespostaDaFicha>;
    resumoSugerido?: string;
    /** O resumo que o profissional aprovou. */
    resumo?: string;
    erro?: string;
    fichaId?: string;
    autor?: string;
    criadoEm: string;
    decididaEm?: string;
}

export interface SegmentoDaFala {
    falante: string;
    texto: string;
}

export interface ParteDaNota {
    ordem: number;
    segundos: number;
    texto: string;
    segmentos?: SegmentoDaFala[];
}

/** O resumo aprovado, como a linha do tempo do prontuário o recebe. */
export interface ResumoDaConsulta {
    notaId: string;
    atendimentoId: string;
    modo: ModoDaNota;
    autor?: string;
    aprovadoEm: string;
    /** Falso quando a clínica não deixa esta pessoa ler: o texto não vem. */
    visivel: boolean;
    resumo?: string;
}

export interface OpcoesDaNovaNota {
    /** Obrigatório no modo consulta: o paciente autorizou a gravação. */
    consentimento: boolean;
    /** Sem modelo, a "Nota da consulta" da clínica. */
    modeloId?: string;
}

/** Duração de cada parte. Um minuto perdido numa queda é o pior caso. */
export const PARTE_MS = 60_000;

/** As mesmas situações do índice `nota_de_ia_aberta` da 0155. */
const ABERTAS: SituacaoDaNota[] = ['gravando', 'gerando', 'revisao', 'falhou'];

export function notaEmAndamento(notas: NotaDeIa[]): NotaDeIa | undefined {
    return notas.find((n) => ABERTAS.includes(n.situacao));
}

/** A transcrição se lê pela ordem das partes, não pela ordem em que chegaram. */
export function textoDaTranscricao(partes: ParteDaNota[]): string {
    return [...partes]
        .sort((a, b) => a.ordem - b.ordem)
        .map((p) => p.texto.trim())
        .filter(Boolean)
        .join('\n\n');
}

export function segundosGravados(partes: { segundos: number }[]): number {
    return partes.reduce((s, p) => s + p.segundos, 0);
}
